import React from 'react';

import { formatMoney } from '@betterment/core';
import { ListRow, Separator, Text } from '@betterment/samba-react';

import { type ActivityItemOrder, ActivityItemStage } from './data';

import { toTitleCase } from '../../../../../utils/StringHelpers';
import { useFeatureFlag } from '../../../../feature-flag/useFeatureFlag';
import { formattedActivityDate } from '../../../utils/dateUtils';
import { getLatestStatus } from '../../../utils/statusUtils';
import { ActivityDrawerLayout } from '../common/ActivityDrawerLayout';
import { DrawerListRow } from '../common/DrawerListRow';

const formattedOrderAmount = (item: ActivityItemOrder) => {
  const amount = item.amountTraded || item.amountRequested;
  const shares = item.sharesTraded || item.sharesRequested;
  if (amount) {
    return formatMoney(amount);
  }
  if (shares) {
    return `${shares} shares`;
  }
  return '—';
};

const formattedOrderDescription = (item: ActivityItemOrder) => {
  if (item.amountRequested && item.sharePrice && item.sharesTraded) {
    return `${item.sharesTraded} shares at ${formatMoney(item.sharePrice)}/share`;
  }
  if (item.sharesRequested && item.sharePrice && item.amountTraded) {
    return `${formatMoney(item.amountTraded)} at ${formatMoney(item.sharePrice)}/share`;
  }
  return undefined;
};

const titleForOrder = (item: ActivityItemOrder) => {
  return item.tradeSide && item.security.symbol ? `${item.security.symbol} ${toTitleCase(item.tradeSide)}` : undefined;
};

export const Order: React.FC<{
  item: ActivityItemOrder;
  activityReportsLink?: React.ReactNode;
  cancelAction?: React.ReactNode;
}> = ({ item, activityReportsLink, cancelAction }) => {
  const { enabled: isAfterHoursCancellationEnabled } = useFeatureFlag({
    splitName: 'retail.self_directed_after_hours_order_cancellation_q4_2025_enabled',
    context: 'order_drawer',
  });
  const latestStatus = getLatestStatus(item.statuses);
  const stage = latestStatus?.stage;
  const stageDisplay = stage === ActivityItemStage.Initiated ? 'Pending' : toTitleCase(stage || '');
  const statusDate = formattedActivityDate(latestStatus?.timestamp || undefined);
  return (
    <ActivityDrawerLayout title={titleForOrder(item) || 'Order'}>
      <DrawerListRow label="Account" value={item.subAccount?.name} />
      <Separator />
      <DrawerListRow label="Amount" value={formattedOrderAmount(item)} valueSubText={formattedOrderDescription(item)} />
      <Separator />
      <DrawerListRow label="Status" value={stageDisplay} valueSubText={statusDate || undefined} />
      <Separator />
      {stage === ActivityItemStage.Initiated && (
        <ListRow css={{ px: '0', pb: 'space.200', pt: '0' }}>
          <ListRow.BottomContent>
            <Text variant="text50" color="text.weak">
              {isAfterHoursCancellationEnabled
                ? 'Orders can be canceled outside of market hours.'
                : 'Trades are typically executed within 30 minutes. If initiated after market hours, they will queue at the start of the next trading day.'}
            </Text>
          </ListRow.BottomContent>
        </ListRow>
      )}
      {activityReportsLink}
      {cancelAction}
    </ActivityDrawerLayout>
  );
};
